/* eslint-disable @typescript-eslint/no-explicit-any */

import { Formalite, useFormaliteService } from '@europrocurement/l2d-domain';
import {
    FormaliteFormaliteUpdatePartial,
    FormaliteJsonldFormaliteCreate,
    FormaliteJsonldFormaliteCreateChargeEnum,
    FormaliteJsonldFormaliteUpdate,
    FormaliteJsonldFormaliteUpdateChargeEnum,
    FormaliteJsonldFormaliteUpdateTypeRecupGreffeEnum,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { UPDATE_ITEM_CTX, useFormalitiesCtxDispatch } from '@europrocurement/l2d-modules';
import { apiToStateTransformer } from '@europrocurement/l2d-modules/providers/FormalitiesProvider';

const useFormalityFormHandler = () => {
    const { formaliteApi } = useFormaliteService();
    const { request, requestState } = useApiRequest();
    const dispatch = useFormalitiesCtxDispatch();

    const updateFormality = async (formality: Formalite, payload: FormaliteJsonldFormaliteUpdate) =>
        new Promise((resolve) => {
            request(
                formaliteApi.apiFormalitesIdPut({
                    id: formality.id as unknown as string,
                    formaliteJsonldFormaliteUpdate: {
                        ...{
                            categorie: formality?.categorie?.id as unknown as string,
                            libelle: formality.libelle,
                            informationsSupplementaires: formality.informationsSupplementaires,
                            iconeName: formality.iconeName,
                            deleted: formality.deleted,
                            legalStatuses: formality.legalStatuses,
                            compatibilities: formality.compatibilities,
                            documents: formality.documents,
                            validators: formality.validators,
                            formulaire: formality.formulaire,
                            charge: formality.charge as FormaliteJsonldFormaliteUpdateChargeEnum,
                            rang: formality.rang,
                            typeRecupGreffe: formality.typeRecupGreffe,
                            sirenRequired: Boolean(formality?.sirenRequired),
                            simpleOrderPath: Boolean(formality?.simpleOrderPath),
                        },
                        ...payload,
                    },
                }),
                {
                    successCallback: (response) => {
                        if (dispatch) {
                            dispatch({
                                type: UPDATE_ITEM_CTX,
                                payload: apiToStateTransformer(response.data),
                            });
                        }
                        resolve(response);
                    },
                },
            );
        });
    const updatePartialFormality = async (
        formality: Formalite,
        payload: FormaliteFormaliteUpdatePartial,
    ) =>
        new Promise((resolve) => {
            request(
                formaliteApi.apiFormalitesIdPatch({
                    id: formality.id as unknown as string,
                    formaliteFormaliteUpdatePartial: {
                        ...{
                            categorie: formality?.categorie?.id as unknown as string,
                            libelle: formality.libelle,
                            informationsSupplementaires: formality.informationsSupplementaires,
                            iconeName: formality.iconeName,
                            deleted: formality.deleted,
                            formulaire: formality.formulaire,
                            charge: formality.charge as FormaliteJsonldFormaliteUpdateChargeEnum,
                            rang: formality.rang,
                            typeRecupGreffe: formality.typeRecupGreffe,
                            sirenRequired: Boolean(formality?.sirenRequired),
                            simpleOrderPath: Boolean(formality?.simpleOrderPath),
                        },
                        ...payload,
                    },
                }),
                {
                    successCallback: (response) => {
                        if (dispatch) {
                            dispatch({
                                type: UPDATE_ITEM_CTX,
                                payload: apiToStateTransformer(response.data),
                            });
                        }
                        resolve(response);
                    },
                },
            );
        });

    const handleCreateForm = async (formData: { [key: string]: any }) =>
        new Promise((resolve) => {
            const {
                categorie,
                libelle,
                informationsSupplementaires,
                iconeName,
                formulaire,
                rang,
                charge,
                typeRecupGreffe,
            } = formData;

            const payload: FormaliteJsonldFormaliteCreate = {
                categorie: categorie?.id,
                libelle,
                informationsSupplementaires,
                iconeName,
                deleted: false,
                documents: [],
                formulaire: formulaire?.id,
                rang: Number(rang),
                charge: charge.value as FormaliteJsonldFormaliteCreateChargeEnum,
                typeRecupGreffe:
                    typeRecupGreffe.value as FormaliteJsonldFormaliteUpdateTypeRecupGreffeEnum,
            };

            request(
                formaliteApi.apiFormalitesPost({
                    formaliteJsonldFormaliteCreate: payload,
                }),
                {
                    successCallback: resolve,
                },
            );
        });

    const handleEditForm = async (formality: Formalite, formData: { [key: string]: any }) => {
        const {
            libelle,
            informationsSupplementaires,
            categorie,
            iconeName,
            formulaire,
            rang,
            charge,
            typeRecupGreffe,
            sirenRequired,
            simpleOrderPath,
        } = formData;
        const payload: FormaliteFormaliteUpdatePartial = {
            categorie: categorie?.id,
            libelle,
            informationsSupplementaires,
            iconeName,
            formulaire: formulaire?.id,
            rang: Number(rang),
            charge: charge.value as FormaliteJsonldFormaliteUpdateChargeEnum,
            typeRecupGreffe:
                typeRecupGreffe.value as FormaliteJsonldFormaliteUpdateTypeRecupGreffeEnum,
            sirenRequired: Boolean(sirenRequired?.value),
            simpleOrderPath: Boolean(simpleOrderPath?.value),
        };

        return updatePartialFormality(formality, payload);
    };

    return {
        handleCreateForm,
        handleEditForm,
        updateFormality,
        updatePartialFormality,
        requestState,
    };
};

export default useFormalityFormHandler;
